/**
 * @fileoverview Fluent-inspired dimensional entity and inspection artwork catalog.
 * @module ui-artwork/catalog/threeD
 *
 * Responsibilities: Register stable 3D IDs, labels, keywords, aliases, and
 *   their visual color policy.
 * Dependencies: ArtworkCatalogEntry.
 * Architecture: Static catalog data; SVG rendering is owned by the package.
 * Notes: Artwork is authored and bundled locally; no runtime asset URLs.
 */
import type { ArtworkCatalogEntry, ArtworkCategory } from '../types';

const make = (
  id: string,
  label: string,
  category: ArtworkCategory,
  keywords: string[],
  alias?: string,
): ArtworkCatalogEntry => ({
  id: `art:3d/${id}`,
  ...(alias ? { legacyAliases: [alias] } : {}),
  label,
  category,
  style: '3d',
  keywords: [label.toLowerCase(), ...keywords],
  colorPolicy: ['hazard-flame', 'hazard-biohazard', 'hazard-radioactive'].includes(id) ? 'fixed' : 'customizable',
  defaultColor: ['hazard-flame'].includes(id) ? '#dc2626' : '#2563eb',
  source: 'fluent-3d',
  attribution: 'Fluent-inspired authored SVG (style reference: Microsoft Fluent UI Emoji, MIT)',
});

export const THREE_D_ARTWORK: ArtworkCatalogEntry[] = [
  make('residential-house', 'Residential house', 'entity', ['home', 'residence', 'building'], 'house'),
  make('wildland-house-tree', 'Wildland house with trees', 'inspection', ['wildland', 'house', 'tree', 'wui']),
  make('commercial-office', 'Commercial office', 'entity', ['office', 'commercial', 'building'], 'building'),
  make('industrial-factory', 'Industrial factory', 'entity', ['factory', 'industrial']),
  make('educational-school', 'Educational school', 'entity', ['school', 'education']),
  make('healthcare-hospital', 'Healthcare hospital', 'entity', ['hospital', 'healthcare', 'medical']),
  make('mercantile-store', 'Mercantile store', 'entity', ['store', 'retail', 'shop']),
  make('hospitality-hotel', 'Hospitality hotel', 'entity', ['hotel', 'lodging']),
  make('assembly-stadium', 'Assembly stadium', 'entity', ['stadium', 'assembly', 'arena']),
  {
    ...make('derelict-vacant', 'Derelict vacant structure', 'entity', ['vacant', 'abandoned', 'derelict']),
    legacyAliases: ['art:3d/Derelict-vacant'],
  },
  make('wildland-evergreen', 'Wildland evergreen', 'inspection', ['evergreen', 'pine', 'timber']),
  make('apparatus-engine', 'Fire engine', 'equipment', ['apparatus', 'fire truck', 'engine'], 'fire-engine'),
  make('apparatus-ambulance', 'Ambulance', 'equipment', ['ems', 'emergency medical']),
  make('equip-extinguisher', 'Fire extinguisher', 'equipment', ['extinguisher', 'fire']),
  make('hazard-flame', 'Flame', 'hazard', ['fire', 'flame']),
  make('hazard-biohazard', 'Biohazard', 'hazard', ['biological', 'biohazard']),
  make('hazard-radioactive', 'Radioactive hazard', 'hazard', ['radiation', 'radioactive']),
  make('workflow-calendar', 'Schedule calendar', 'status', ['calendar', 'schedule']),
  make('workflow-clipboard', 'Inspection clipboard', 'inspection', ['clipboard', 'inspection', 'checklist']),
  make('workflow-certificate', 'Permit certificate', 'status', ['permit', 'certificate', 'scroll']),
];
